import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import {
  FENCE_RISK_COLORS,
  RiskClassification,
  DEFAULT_FENCE_RISK_CLASSIFICATION,
  DEFAULT_FENCE_NOTIFICATION_ENABLED,
  FENCE_RISK_NOTIFICATION_DEFAULT,
} from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { createEntity, getEntity, reset, updateEntity } from 'app/entities/virtual-fence-polygon/virtual-fence-polygon.reducer';
import { getEntities as getPolygonEntities } from 'app/entities/virtual-fence-polygon/virtual-fence-polygon.reducer';
import { getEntitiesForMap as getPolygonEntitiesForMap } from 'app/entities/virtual-fence-polygon/virtual-fence-polygon.reducer';
import { getEntities as getCircleEntities } from 'app/entities/virtual-fence-circle/virtual-fence-circle.reducer';
import {
  getStorageOptions,
  IdStorageTypes,
  setStorageOptions,
  ValuesStorageTypes,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-journey-tab/storageOptions';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import { AutoCompleteSearch } from 'app/shared/components/autocomplete-search/AutoCompleteSearch';
import { IVirtualFencePolygon, IVirtualFencePolygonPoint } from 'app/shared/model/virtual-fence-polygon.model';
import { IVirtualFenceCircle, VirtualFenceSyncStatus } from 'app/shared/model/virtual-fence-circle.model';
import { ITruckGroup } from 'app/shared/model/truck-group.model';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, Polyline, withGoogleMap, withScriptjs } from 'react-google-maps';
import CircleComponent from 'react-google-maps/lib/components/Circle';
import PolygonComponent from 'react-google-maps/lib/components/Polygon';
import StreetViewPanorama from 'react-google-maps/lib/components/StreetViewPanorama';
import { toast } from 'react-toastify';
import { Spinner } from 'reactstrap';
import { compose, withProps } from 'recompose';
import { VirtualFencePolygonForms } from './VirtualFencePolygonForms';

const MIN_POINTS = 3;
const MAX_POINTS = 10;

const G_API_URL = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&&v=3.exp&libraries=geometry,drawing,places`;

type LatLng = { lat: number; lng: number };

const pointsToLatLng = (points: IVirtualFencePolygonPoint[]): LatLng[] =>
  [...points]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .filter((p): p is IVirtualFencePolygonPoint & { latitude: number; longitude: number } => p.latitude != null && p.longitude != null)
    .map(p => ({ lat: p.latitude, lng: p.longitude }));

const latLngToPoints = (vertices: LatLng[]): IVirtualFencePolygonPoint[] => vertices.map((v, i) => ({ position: i, latitude: v.lat, longitude: v.lng }));

const defaultCenter: LatLng = { lat: -15.79956, lng: -47.90032 };

const getPolygonOptions = (color: string) => ({
  fillColor: color,
  fillOpacity: 0.5,
  strokeColor: color,
  strokeWeight: 2,
  clickable: false,
  zIndex: 1,
});

const isPendingFenceSync = (syncStatus?: VirtualFenceSyncStatus | null) => syncStatus === 'PENDING_SYNC' || syncStatus === 'PENDING_DELETE';

const VirtualFencePolygonMapInner = ({ props }: { props: any }) => {
  const dispatch = useAppDispatch();
  const mapRef = useRef<any>(null);
  const pendingInitialEditBoundsRef = useRef<LatLng[] | null>(null);

  const isNew = !props.match.params?.id;

  const [riskClassification, setRiskClassificationState] = useState<RiskClassification>(DEFAULT_FENCE_RISK_CLASSIFICATION);
  const [notificationEnabled, setNotificationEnabledState] = useState<boolean>(DEFAULT_FENCE_NOTIFICATION_ENABLED);
  const notificationManuallyChangedRef = useRef(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [searchLatitude, setSearchLatitude] = useState<number | null>(null);
  const [searchLongitude, setSearchLongitude] = useState<number | null>(null);
  const handleAddressSelected = useCallback(() => undefined, []);

  const setRiskClassification = (v: RiskClassification) => {
    setRiskClassificationState(v);
    if (!notificationManuallyChangedRef.current) {
      setNotificationEnabledState(FENCE_RISK_NOTIFICATION_DEFAULT[v]);
    }
  };

  const setNotificationEnabled = (v: boolean) => {
    notificationManuallyChangedRef.current = true;
    setNotificationEnabledState(v);
  };

  const [mapType, setMapType] = useState<ValuesStorageTypes>(
    getStorageOptions(IdStorageTypes.MAP_TYPE) === ValuesStorageTypes.SATELLITE ? ValuesStorageTypes.SATELLITE : ValuesStorageTypes.MAP
  );

  const [vertices, setVertices] = useState<LatLng[]>([]);
  const [polygonClosed, setPolygonClosed] = useState(false);
  const googleMapOptions = useMemo(
    () => ({
      styles: mapStyle,
      draggableCursor: polygonClosed ? 'grab' : 'crosshair',
      draggingCursor: 'grabbing',
    }),
    [polygonClosed]
  );

  const loading = useAppSelector(state => state.virtualFencePolygon.loading);
  const updateSuccess = useAppSelector(state => state.virtualFencePolygon.updateSuccess);
  const entity = useAppSelector(state => state.virtualFencePolygon.entity) as IVirtualFencePolygon;
  const allPolygons = useAppSelector(state => state.virtualFencePolygon.entities) as IVirtualFencePolygon[];
  const allCircles = useAppSelector(state => state.virtualFenceCircle.entities) as IVirtualFenceCircle[];
  const [showAllFences, setShowAllFences] = useState(false);
  const readOnlyFromList = new URLSearchParams(props.location.search).get('readOnlyPending') === 'true';
  const readOnly = !isNew && (readOnlyFromList || isPendingFenceSync(entity?.syncStatus));

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(props.match.params.id));
    }
  }, []);

  useEffect(() => {
    if (showAllFences) {
      dispatch(getPolygonEntitiesForMap());
      dispatch(getCircleEntities({}));
    }
  }, [showAllFences]);

  const setEditValues = useCallback(() => {
    if (!entity?.id) return;
    setName(entity.name ?? '');
    setDescription(entity.description ?? '');
    setRiskClassificationState(entity.riskClassification ?? DEFAULT_FENCE_RISK_CLASSIFICATION);
    setNotificationEnabledState(entity.notificationEnabled ?? false);
    notificationManuallyChangedRef.current = true;
    if (entity.points?.length) {
      const entityVertices = pointsToLatLng(entity.points);
      pendingInitialEditBoundsRef.current = entityVertices;
      setVertices(entityVertices);
      setPolygonClosed(entityVertices.length >= MIN_POINTS);
    }
    if (entity.truckGroups) {
      setSelectedGroupIds(entity.truckGroups.map((g: ITruckGroup) => g.id).filter(Boolean));
    }
  }, [entity]);

  useEffect(() => {
    if (!isNew) setEditValues();
  }, [setEditValues]);
  useEffect(() => {
    fitInitialEditBounds();
  }, [vertices]);

  const handleClose = () => {
    dispatch(reset());
    const returnTo = props.location.state?.returnTo;
    if (returnTo === '/reports/passages') props.history.replace(returnTo);
    else props.history.goBack();
  };
  useEffect(() => {
    if (updateSuccess) handleClose();
  }, [updateSuccess]);

  useEffect(() => {
    if (searchLatitude == null || searchLongitude == null || !mapRef.current) return;

    const googleMaps = (window as any).google?.maps;
    if (!googleMaps || typeof mapRef.current.fitBounds !== 'function') return;

    const bounds = new googleMaps.LatLngBounds();
    const offset = 0.005;
    bounds.extend({ lat: searchLatitude + offset, lng: searchLongitude + offset });
    bounds.extend({ lat: searchLatitude - offset, lng: searchLongitude - offset });
    mapRef.current.fitBounds(bounds);
  }, [searchLatitude, searchLongitude]);

  const handleMapClick = (event: any) => {
    if (readOnly) return;
    if (polygonClosed) return;
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setVertices(prev => {
      if (prev.length >= MAX_POINTS) {
        toast.warning(`Máximo de ${MAX_POINTS} pontos atingido`);
        return prev;
      }
      return [...prev, { lat, lng }];
    });
  };

  const handleClosePolygon = () => {
    if (readOnly) return;
    if (vertices.length < MIN_POINTS) {
      toast.info(`Adicione pelo menos ${MIN_POINTS} pontos antes de fechar o polígono`);
      return;
    }
    setPolygonClosed(true);
  };

  const handleUndoPoint = () => {
    if (!readOnly) {
      setPolygonClosed(false);
      setVertices(prev => prev.slice(0, -1));
    }
  };
  const handleClearPoints = () => {
    if (!readOnly) {
      setVertices([]);
      setPolygonClosed(false);
    }
  };

  const handlePolygonMouseUp = (polygonRef: any) => {
    if (readOnly) return;
    if (!polygonRef?.getPath) return;
    setTimeout(() => {
      const path = polygonRef.getPath();
      if (!path) return;
      const updated: LatLng[] = [];
      for (let i = 0; i < path.getLength(); i++) {
        const latlng = path.getAt(i);
        updated.push({ lat: latlng.lat(), lng: latlng.lng() });
      }
      if (updated.length > MAX_POINTS) {
        toast.warning(`Máximo de ${MAX_POINTS} pontos atingido`);
        setVertices(prev => {
          if (polygonRef?.setPath) {
            polygonRef.setPath(prev);
          } else if (path?.clear && path?.push) {
            path.clear();
            prev.forEach(point => path.push(new google.maps.LatLng(point.lat, point.lng)));
          }
          return [...prev];
        });
        return;
      }
      setVertices(prev => {
        if (
          updated.length === prev.length &&
          updated.every((v, i) => Math.abs(v.lat - (prev[i]?.lat ?? 0)) < 1e-10 && Math.abs(v.lng - (prev[i]?.lng ?? 0)) < 1e-10)
        )
          return prev;
        return updated;
      });
      if (updated.length < MIN_POINTS) setPolygonClosed(false);
    }, 0);
  };

  const onClickMapType = () => {
    document.getElementsByClassName('gm-style-mtc')[0]?.addEventListener('click', () => {
      setMapType(ValuesStorageTypes.MAP);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.MAP);
    });
    document.getElementsByClassName('gm-style-mtc')[1]?.addEventListener('click', () => {
      setMapType(ValuesStorageTypes.SATELLITE);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.SATELLITE);
    });
  };

  const fitBoundsToVertices = (verts: LatLng[]) => {
    if (!mapRef.current || !(window as any).google?.maps || verts.length < 2) return false;
    const bounds = new (window as any).google.maps.LatLngBounds();
    verts.forEach(v => bounds.extend(v));
    mapRef.current.fitBounds(bounds);
    return true;
  };

  const fitInitialEditBounds = () => {
    if (isNew || !pendingInitialEditBoundsRef.current) return;
    if (fitBoundsToVertices(pendingInitialEditBoundsRef.current)) pendingInitialEditBoundsRef.current = null;
  };

  const handleSave = ({
    name: n,
    description: d,
    riskClassification: rc,
    notificationEnabled: ne,
  }: {
    name: string;
    description: string;
    riskClassification: RiskClassification;
    notificationEnabled: boolean;
  }) => {
    if (readOnly) return;
    if (vertices.length < MIN_POINTS) return toast.error(`Adicione pelo menos ${MIN_POINTS} pontos ao polígono`);
    if (vertices.length > MAX_POINTS) return toast.error(`O polígono não pode ter mais de ${MAX_POINTS} pontos`);
    if (!polygonClosed) return toast.error('Clique novamente no primeiro ponto para fechar o polígono');
    const first = vertices[0];
    const last = vertices[vertices.length - 1];
    if (!first || !last) return;
    if (Math.abs(first.lat - last.lat) < 1e-8 && Math.abs(first.lng - last.lng) < 1e-8)
      return toast.error('O ponto final não pode ser igual ao ponto inicial (o polígono já fecha automaticamente)');

    const payload: IVirtualFencePolygon = {
      ...(isNew ? {} : entity),
      name: n,
      description: d,
      riskClassification: rc,
      notificationEnabled: ne,
      activated: true,
      points: latLngToPoints(vertices),
      truckGroups: selectedGroupIds.map(id => ({ id } as ITruckGroup)),
      ...(isNew && new URLSearchParams(props.location.search).get('companyId')
        ? { company: { id: Number(new URLSearchParams(props.location.search).get('companyId')) } }
        : {}),
    };

    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  const theme = useTheme();
  const badgeBg = theme.palette.background.paper;
  const badgeColor = theme.palette.text.primary;
  const badgeBorder = theme.palette.divider;

  const renderLoading = () =>
    loading ? (
      <div className="spinner-map">
        <Spinner color="info" />
      </div>
    ) : null;

  const renderExistingFences = () => {
    if (!showAllFences) return null;
    const editingId = isNew ? null : entity?.id;
    const circles = allCircles
      .filter(f => f.latitude != null && f.longitude != null && f.activated)
      .map(f => {
        const color = f.color ?? FENCE_RISK_COLORS[DEFAULT_FENCE_RISK_CLASSIFICATION];
        const r = Math.min(Math.max(f.radius ?? 250, 30), 65500);
        return (
          <CircleComponent
            key={`c-${f.id}`}
            center={{ lat: f.latitude, lng: f.longitude }}
            radius={r}
            options={{ fillColor: color, fillOpacity: 0.25, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 0.8, zIndex: 0 }}
          />
        );
      });
    const polygons = allPolygons
      .filter(f => f.activated && f.id !== editingId && (f.points?.length ?? 0) >= 3)
      .map(f => {
        const path = [...(f.points ?? [])]
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          .filter((p): p is typeof p & { latitude: number; longitude: number } => p.latitude != null && p.longitude != null)
          .map(p => ({ lat: p.latitude, lng: p.longitude }));
        if (path.length < 3) return null;
        const color = f.color ?? FENCE_RISK_COLORS[DEFAULT_FENCE_RISK_CLASSIFICATION];
        return (
          <PolygonComponent
            key={`p-${f.id}`}
            path={path}
            options={{ fillColor: color, fillOpacity: 0.25, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 0.8, zIndex: 0 }}
          />
        );
      });
    return (
      <>
        {circles}
        {polygons}
      </>
    );
  };
  const polygonRef = useRef<any>(null);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '400px minmax(0, 1fr)', xl: '420px minmax(0, 1fr)' }, gap: 1.5, alignItems: 'stretch' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          p: { xs: 1.5, sm: 2.25 },
          height: { xs: 'auto', md: '72vh' },
          overflowY: 'auto',
          minWidth: 0,
        }}
      >
        <VirtualFencePolygonForms
          onSave={handleSave}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          riskClassification={riskClassification}
          setRiskClassification={setRiskClassification}
          notificationEnabled={notificationEnabled}
          setNotificationEnabled={setNotificationEnabled}
          selectedGroupIds={selectedGroupIds}
          setSelectedGroupIds={setSelectedGroupIds}
          pointCount={vertices.length}
          polygonClosed={polygonClosed}
          onUndoPoint={handleUndoPoint}
          onClearPoints={handleClearPoints}
          readOnly={readOnly}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: { xs: '55vh', md: '72vh' },
          minHeight: 360,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <Box sx={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 320, bgcolor: 'background.paper', borderRadius: '10px', boxShadow: 4 }}>
            <AutoCompleteSearch setLatitude={setSearchLatitude} setLongitude={setSearchLongitude} setIsAutoComplete={handleAddressSelected} />
          </Box>
          <Tooltip title={showAllFences ? 'Ocultar cercas existentes' : 'Exibir cercas existentes'} placement="bottom">
            <IconButton
              onClick={() => setShowAllFences(v => !v)}
              sx={{
                bgcolor: showAllFences ? 'primary.main' : 'background.paper',
                color: showAllFences ? 'primary.contrastText' : 'text.primary',
                boxShadow: 3,
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
                '&:hover': { bgcolor: showAllFences ? 'primary.dark' : 'action.hover' },
              }}
            >
              <LayersRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {!loading && (
          <Box
            role="status"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: '6px',
              bgcolor: badgeBg,
              color: badgeColor,
              border: '1px solid',
              borderColor: badgeBorder,
              boxShadow: 3,
              fontSize: '0.78rem',
              fontWeight: 400,
              lineHeight: 1.35,
              width: 'max-content',
              maxWidth: 'calc(100% - 100px)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            {polygonClosed ? <CheckCircleRoundedIcon sx={{ fontSize: 17 }} /> : <TouchAppRoundedIcon sx={{ fontSize: 17 }} />}
            <span>
              {readOnly
                ? 'Cerca pendente de sincronização. Os campos e pontos estão bloqueados para edição.'
                : polygonClosed
                ? 'Polígono fechado. Arraste os pontos para ajustar.'
                : vertices.length === 0
                ? 'Clique no mapa para marcar o primeiro ponto.'
                : vertices.length >= MIN_POINTS
                ? 'Continue ou clique no 1º ponto para fechar.'
                : `Adicione mais ${MIN_POINTS - vertices.length} ${MIN_POINTS - vertices.length === 1 ? 'ponto' : 'pontos'}.`}
            </span>
          </Box>
        )}

        <PolygonGoogleMap
          mapRef={mapRef}
          mapType={mapType}
          mapOptions={googleMapOptions}
          vertices={vertices}
          polygonClosed={polygonClosed}
          color={FENCE_RISK_COLORS[riskClassification] ?? FENCE_RISK_COLORS[DEFAULT_FENCE_RISK_CLASSIFICATION]}
          loading={loading}
          readOnly={readOnly}
          polygonRef={polygonRef}
          onTilesLoaded={fitInitialEditBounds}
          onMapTypeIdChanged={onClickMapType}
          onClick={handleMapClick}
          onClosePolygon={handleClosePolygon}
          onPolygonMouseUp={() => handlePolygonMouseUp(polygonRef.current)}
          renderLoading={renderLoading}
          renderExistingFences={renderExistingFences}
        />
      </Box>
    </Box>
  );
};

const PolygonGoogleMapComponent = ({
  mapRef,
  mapType,
  mapOptions,
  vertices,
  polygonClosed,
  color,
  loading,
  readOnly,
  polygonRef,
  onTilesLoaded,
  onMapTypeIdChanged,
  onClick,
  onClosePolygon,
  onPolygonMouseUp,
  renderLoading,
  renderExistingFences,
}: any) => (
  <GoogleMap
    ref={mapRef}
    defaultZoom={4.6}
    defaultCenter={defaultCenter}
    options={mapOptions}
    onTilesLoaded={onTilesLoaded}
    onMapTypeIdChanged={onMapTypeIdChanged}
    mapTypeId={google.maps.MapTypeId[mapType]}
    onClick={onClick}
  >
    <StreetViewPanorama visible={false} />
    {renderLoading()}
    {!loading && renderExistingFences()}
    {!loading && !readOnly && !polygonClosed && vertices.length > 0 && (
      <Marker
        position={vertices[0]}
        icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }}
        title={vertices.length >= MIN_POINTS ? 'Clique para fechar o polígono' : 'Primeiro ponto'}
        clickable
        onClick={onClosePolygon}
      />
    )}
    {!loading && !polygonClosed && vertices.length >= 2 && <Polyline path={vertices} options={{ strokeColor: color, strokeWeight: 2, zIndex: 1 }} />}
    {!loading && polygonClosed && vertices.length >= MIN_POINTS && (
      <PolygonComponent
        ref={polygonRef}
        path={vertices}
        editable={!readOnly}
        options={getPolygonOptions(color)}
        onMouseUp={readOnly ? undefined : onPolygonMouseUp}
      />
    )}
  </GoogleMap>
);

const PolygonGoogleMap = compose(
  withProps({
    googleMapURL: G_API_URL,
    loadingElement: <div style={{ height: '100%' }} />,
    containerElement: <div style={{ height: '100%' }} />,
    mapElement: <div style={{ height: '100%' }} />,
  }),
  withScriptjs,
  withGoogleMap
)(PolygonGoogleMapComponent);

export default VirtualFencePolygonMapInner;
